import React, { useState, useEffect } from "react";
import MainCalculatorsList from "../OtherCalculator/CountriesMainCalculatorsList/SAMainCalculators";
import RelatedLinks from "../RelatedLinks";
import { SecondAds, FirstAds, MobileUnderCalAds } from "../../Ads";
import SharedHelmet from "../Helmet";

const TITLE = "حاسبة سلم رواتب الموظفين العام 2026 - الدرجة والراتب بعد الترقية";
const DESCRIPTION =
  "احسب راتبك الأساسي في سلم رواتب الموظفين العام السعودي واكتشف درجتك وراتبك بعد الترقية. أدخل مرتبتك الحالية ودرجتك واحصل على النتيجة فوراً.";
const KEYWORDS =
  "سلم رواتب الموظفين, راتب الخدمة المدنية, درجة بعد الترقية, سلم رواتب السعودية, راتب المرتبة, حاسبة الراتب, العلاوة السنوية, مرتبات الموظفين";
const OG_URL =
  "https://do-calculate.com/calculator/ar/countries/SA/calculators-list/salary-scale-calculator/";

// Saudi General Civil Service Salary Scale (السلم العام لرواتب الموظفين)
// Starting salary (degree 1) and annual increment per grade
// Source: وزارة الموارد البشرية والتنمية الاجتماعية
const SALARY_SCALE = {
  1:  { start: 3040.5,  increment: 135,  nameAr: "الأولى"         },
  2:  { start: 3621.3,  increment: 165,  nameAr: "الثانية"        },
  3:  { start: 4206.7,  increment: 190,  nameAr: "الثالثة"        },
  4:  { start: 5049.4,  increment: 230,  nameAr: "الرابعة"        },
  5:  { start: 5862.3,  increment: 265,  nameAr: "الخامسة"        },
  6:  { start: 7098.9,  increment: 350,  nameAr: "السادسة"        },
  7:  { start: 7488.3,  increment: 365,  nameAr: "السابعة"        },
  8:  { start: 8010.0,  increment: 415,  nameAr: "الثامنة"        },
  9:  { start: 9535.0,  increment: 470,  nameAr: "التاسعة"        },
  10: { start: 10752.7, increment: 510,  nameAr: "العاشرة"        },
  11: { start: 13050.0, increment: 600,  nameAr: "الحادية عشرة"   },
  12: { start: 15600.0, increment: 700,  nameAr: "الثانية عشرة"   },
  13: { start: 18600.0, increment: 850,  nameAr: "الثالثة عشرة"   },
  14: { start: 22000.0, increment: 1000, nameAr: "الرابعة عشرة"   },
  15: { start: 26000.0, increment: 1200, nameAr: "الخامسة عشرة"   },
};

const DEGREE_NAMES = {
  1: "الأولى", 2: "الثانية", 3: "الثالثة", 4: "الرابعة", 5: "الخامسة",
  6: "السادسة", 7: "السابعة", 8: "الثامنة", 9: "التاسعة", 10: "العاشرة",
  11: "الحادية عشرة", 12: "الثانية عشرة", 13: "الثالثة عشرة",
  14: "الرابعة عشرة", 15: "الخامسة عشرة",
};

function getSalary(grade, degree) {
  const { start, increment } = SALARY_SCALE[grade];
  return parseFloat((start + (degree - 1) * increment).toFixed(1));
}

function getPromotionResult(currentGrade, currentDegree) {
  const currentSalary = getSalary(currentGrade, currentDegree);
  const nextGrade = currentGrade + 1;
  if (!SALARY_SCALE[nextGrade]) return null;
  for (let d = 1; d <= 15; d++) {
    const newSalary = getSalary(nextGrade, d);
    if (newSalary >= currentSalary) {
      return { grade: nextGrade, degree: d, salary: newSalary };
    }
  }
  const d15salary = getSalary(nextGrade, 15);
  return { grade: nextGrade, degree: 15, salary: d15salary };
}

function SaudiSalaryScaleCalculator() {
  const [currentGrade, setCurrentGrade] = useState("");
  const [currentDegree, setCurrentDegree] = useState("");
  const [result, setResult] = useState(null);

  const handleReset = () => {
    setCurrentGrade("");
    setCurrentDegree("");
  };

  useEffect(() => {
    const g = parseInt(currentGrade);
    const d = parseInt(currentDegree);
    if (!currentGrade || !currentDegree || !SALARY_SCALE[g] || d < 1 || d > 15) {
      setResult(null);
      return;
    }
    const currentSalary = getSalary(g, d);
    const promotion = getPromotionResult(g, d);
    const nextDegree = d < 15 ? d + 1 : null;
    const nextDegreeSalary = nextDegree ? getSalary(g, nextDegree) : null;
    setResult({ currentSalary, promotion, nextDegree, nextDegreeSalary });
  }, [currentGrade, currentDegree]);

  return (
    <div className="rtl" style={{ direction: "rtl" }}>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>

      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ padding: "15px", borderRadius: "5px" }}
          >
            <h1 style={{ fontSize: "22px", margin: 0 }}>{TITLE}</h1>
          </div>
          <div className="border border-light mt-3 p-3 bg-white shadow-sm">
            <div className="mb-4">
              <p style={{ fontSize: "16px", lineHeight: "1.8" }}>
                <strong>حاسبة سلم رواتب الموظفين العام</strong> تتيح لك معرفة راتبك الأساسي الحالي
                وفق مرتبتك ودرجتك في سلم رواتب الخدمة المدنية السعودية، وكذلك الدرجة والراتب الذي
                ستنتقل إليه بعد الترقية للمرتبة التالية. النتيجة تظهر فوراً دون الحاجة للضغط على
                أي زر. السلم المعتمد هو سلم رواتب الموظفين العام الصادر عن وزارة الموارد البشرية
                والتنمية الاجتماعية 2026م.
              </p>
            </div>
            <hr />
            <MobileUnderCalAds />

            <div className="calculatorStyle mb-4">
              <div className="p-3 mb-3 bg-success text-white text-center rounded">
                <h2 style={{ fontSize: "18px", margin: 0 }}>
                  احسب راتبك ودرجتك بعد الترقية — سلم الموظفين العام
                </h2>
              </div>

              <div className="row">
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="currentGrade">المرتبة الحالية</label>
                  <select
                    id="currentGrade"
                    className="form-select"
                    value={currentGrade}
                    onChange={(e) => { setCurrentGrade(e.target.value); setCurrentDegree(""); }}
                  >
                    <option value="">-- اختر المرتبة --</option>
                    {Object.entries(SALARY_SCALE).map(([g, { nameAr }]) => (
                      <option key={g} value={g}>المرتبة {nameAr} ({g})</option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-12 col-md-6">
                  <label htmlFor="currentDegree">الدرجة الحالية</label>
                  <select
                    id="currentDegree"
                    className="form-select"
                    value={currentDegree}
                    onChange={(e) => setCurrentDegree(e.target.value)}
                    disabled={!currentGrade}
                  >
                    <option value="">-- اختر الدرجة --</option>
                    {currentGrade && Object.entries(DEGREE_NAMES).map(([d, nameAr]) => (
                      <option key={d} value={d}>الدرجة {nameAr} ({d})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="text-center mb-3 mt-4">
                <button
                  onClick={handleReset}
                  className="btn btn-secondary"
                  style={{ fontSize: "16px", padding: "10px 30px" }}
                >
                  امسح
                </button>
              </div>

              {result && currentGrade && currentDegree && (
                <div className="mt-4">
                  <h3 style={{ fontSize: "18px", marginBottom: "15px", textAlign: "center" }}>
                    <strong>النتيجة:</strong>
                  </h3>

                  <div className="mb-3">
                    <table className="table bmi-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th className="bmi-header" style={{ textAlign: "center" }}>البيان</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>التفاصيل</th>
                          <th className="bmi-header" style={{ textAlign: "center" }}>الراتب الأساسي</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ textAlign: "center" }}>وضعك الحالي</td>
                          <td style={{ textAlign: "center" }}>
                            المرتبة {SALARY_SCALE[parseInt(currentGrade)].nameAr} — الدرجة {DEGREE_NAMES[parseInt(currentDegree)]}
                          </td>
                          <td style={{ textAlign: "center", fontSize: "18px", fontWeight: "bold", color: "#1a5f3f" }}>
                            {result.currentSalary.toLocaleString("ar-SA")} ريال
                          </td>
                        </tr>
                        {result.nextDegree && (
                          <tr>
                            <td style={{ textAlign: "center" }}>العلاوة الدورية (الدرجة التالية)</td>
                            <td style={{ textAlign: "center" }}>
                              المرتبة {SALARY_SCALE[parseInt(currentGrade)].nameAr} — الدرجة {DEGREE_NAMES[result.nextDegree]}
                            </td>
                            <td style={{ textAlign: "center", fontSize: "16px", fontWeight: "bold", color: "#0f3d28" }}>
                              {result.nextDegreeSalary.toLocaleString("ar-SA")} ريال
                            </td>
                          </tr>
                        )}
                        {result.promotion ? (
                          <tr>
                            <td style={{ textAlign: "center" }}><strong>بعد الترقية للمرتبة الأعلى</strong></td>
                            <td style={{ textAlign: "center" }}>
                              <strong>المرتبة {SALARY_SCALE[result.promotion.grade].nameAr} — الدرجة {DEGREE_NAMES[result.promotion.degree]}</strong>
                            </td>
                            <td style={{ textAlign: "center", fontSize: "20px", fontWeight: "bold", color: "#0f3d28" }}>
                              <strong>{result.promotion.salary.toLocaleString("ar-SA")} ريال</strong>
                            </td>
                          </tr>
                        ) : (
                          <tr>
                            <td colSpan="3" style={{ textAlign: "center", color: "#856404" }}>
                              أنت في أعلى مرتبة في السلم (المرتبة الخامسة عشرة) — لا توجد ترقية للمرتبة الأعلى
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="info-sections-container">
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>ملخص النتيجة</strong>
                      </div>
                      <div className="info-section-body">
                        <ul style={{ listStyle: "none", padding: 0, lineHeight: "2" }}>
                          <li>
                            💰 <strong>راتبك الحالي:</strong>{" "}
                            {result.currentSalary.toLocaleString("ar-SA")} ريال
                          </li>
                          {result.nextDegree && (
                            <li>
                              📈 <strong>العلاوة الدورية (الدرجة {DEGREE_NAMES[result.nextDegree]}):</strong>{" "}
                              {result.nextDegreeSalary.toLocaleString("ar-SA")} ريال
                              {" "}(زيادة {(result.nextDegreeSalary - result.currentSalary).toLocaleString("ar-SA")} ريال)
                            </li>
                          )}
                          {!result.nextDegree && (
                            <li>⚠️ أنت في الدرجة الخامسة عشرة (الحد الأعلى لمرتبتك)</li>
                          )}
                          {result.promotion && (
                            <li>
                              🎯 <strong>بعد الترقية — المرتبة {SALARY_SCALE[result.promotion.grade].nameAr}، الدرجة {DEGREE_NAMES[result.promotion.degree]}:</strong>{" "}
                              {result.promotion.salary.toLocaleString("ar-SA")} ريال
                              {" "}(زيادة {(result.promotion.salary - result.currentSalary).toLocaleString("ar-SA")} ريال)
                            </li>
                          )}
                        </ul>
                        <div className="alert alert-info mt-3" style={{ fontSize: "14px" }}>
                          <strong>💡 ملاحظة:</strong> عند الترقية للمرتبة الأعلى، يُمنح الموظف أول درجة في المرتبة الجديدة
                          التي يكون راتبها مساوياً أو أعلى من راتبه الحالي وفق نظام الخدمة المدنية.
                        </div>
                      </div>
                    </div>
                    <div className="info-section">
                      <div className="info-section-header">
                        <strong>تفاصيل الحساب</strong>
                      </div>
                      <div className="info-section-body">
                        <div style={{ lineHeight: "1.8" }}>
                          <p>
                            <strong>المرتبة {SALARY_SCALE[parseInt(currentGrade)].nameAr}:</strong>{" "}
                            الراتب الابتدائي {SALARY_SCALE[parseInt(currentGrade)].start.toLocaleString("ar-SA")} ريال،
                            العلاوة السنوية {SALARY_SCALE[parseInt(currentGrade)].increment.toLocaleString("ar-SA")} ريال
                          </p>
                          <p>
                            <strong>راتب الدرجة {DEGREE_NAMES[parseInt(currentDegree)]}:</strong>{" "}
                            {SALARY_SCALE[parseInt(currentGrade)].start.toLocaleString("ar-SA")} +{" "}
                            ({parseInt(currentDegree) - 1} × {SALARY_SCALE[parseInt(currentGrade)].increment.toLocaleString("ar-SA")})
                            = <strong>{result.currentSalary.toLocaleString("ar-SA")} ريال</strong>
                          </p>
                          {result.promotion && (
                            <>
                              <hr />
                              <p>
                                <strong>المرتبة {SALARY_SCALE[result.promotion.grade].nameAr} (بعد الترقية):</strong>{" "}
                                الراتب الابتدائي {SALARY_SCALE[result.promotion.grade].start.toLocaleString("ar-SA")} ريال،
                                العلاوة السنوية {SALARY_SCALE[result.promotion.grade].increment.toLocaleString("ar-SA")} ريال
                              </p>
                              <p>
                                <strong>أول درجة ≥ راتبك الحالي هي الدرجة {DEGREE_NAMES[result.promotion.degree]}:</strong>{" "}
                                {result.promotion.salary.toLocaleString("ar-SA")} ريال
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
            </div>
          </div>
        </div>

        <div className="col-sm-12 col-md-4">
          <SecondAds />
          <br />
          <MainCalculatorsList />
        </div>
      </div>

      <div className="row" style={{ marginTop: "20px" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">

            <div className="info-section">
              <div className="info-section-header">
                <strong>كيفية استخدام حاسبة سلم الرواتب</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  <strong>حاسبة سلم رواتب الموظفين العام</strong> تحسب تلقائياً راتبك الأساسي
                  وما ستحصل عليه عند العلاوة الدورية أو الترقية للمرتبة الأعلى، دون الحاجة للضغط
                  على أي زر.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px" }}>خطوات الاستخدام:</h3>
                <ol style={{ lineHeight: "1.8" }}>
                  <li>اختر مرتبتك الحالية من القائمة (من الأولى حتى الخامسة عشرة)</li>
                  <li>اختر درجتك الحالية داخل هذه المرتبة (من الدرجة الأولى حتى الخامسة عشرة)</li>
                  <li>تظهر النتيجة تلقائياً: راتبك الحالي، راتب الدرجة التالية، وراتبك بعد الترقية</li>
                </ol>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>ما هو سلم رواتب الموظفين العام؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  سلم رواتب الموظفين العام هو الجدول الرسمي الذي يحدد الراتب الأساسي لكل موظف
                  حكومي في المملكة العربية السعودية. يتكون السلم من <strong>15 مرتبة</strong>، وكل
                  مرتبة تضم <strong>15 درجة</strong> (خطوة راتبية). ينتقل الموظف من درجة إلى أخرى
                  سنوياً بموجب العلاوة الدورية، وينتقل من مرتبة إلى أعلى عند استيفاء شروط الترقية.
                </p>
                <h3 style={{ fontSize: "17px", marginTop: "15px", marginBottom: "10px" }}>العلاوة الدورية السنوية حسب المرتبة:</h3>
                <table className="table bmi-table" style={{ width: "100%" }}>
                  <thead>
                    <tr>
                      <th className="bmi-header" style={{ textAlign: "center" }}>المرتبة</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>الراتب الابتدائي (ريال)</th>
                      <th className="bmi-header" style={{ textAlign: "center" }}>العلاوة السنوية (ريال)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(SALARY_SCALE).map(([g, { nameAr, start, increment }]) => (
                      <tr key={g}>
                        <td style={{ textAlign: "center" }}>المرتبة {nameAr}</td>
                        <td style={{ textAlign: "center" }}>{start.toLocaleString("ar-SA")}</td>
                        <td style={{ textAlign: "center" }}>{increment.toLocaleString("ar-SA")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>كيف تُحسب الدرجة بعد الترقية؟</strong>
              </div>
              <div className="info-section-body">
                <p style={{ lineHeight: "1.8" }}>
                  وفق نظام الخدمة المدنية السعودي، عند ترقية الموظف من مرتبته الحالية إلى المرتبة
                  الأعلى، يبدأ في <strong>أول درجة</strong> في المرتبة الجديدة التي يكون راتبها
                  <strong>مساوياً أو أعلى</strong> من راتبه الأخير. هذا يضمن عدم انخفاض الراتب
                  أبداً عند الترقية.
                </p>
                <div className="alert alert-info mt-3" style={{ lineHeight: "1.8" }}>
                  <strong>💡 مثال:</strong> موظف في المرتبة الأولى الدرجة الثانية (راتبه 3,175.5 ريال)
                  عند ترقيته للمرتبة الثانية يُعيَّن في الدرجة الأولى منها (راتبها 3,621.3 ريال)
                  لأنها أعلى من راتبه الحالي.
                </div>
              </div>
            </div>

            <div className="info-section">
              <div className="info-section-header">
                <strong>شروط الترقية في الخدمة المدنية السعودية</strong>
              </div>
              <div className="info-section-body">
                <ul style={{ lineHeight: "1.8" }}>
                  <li>إكمال المدة النظامية في المرتبة الحالية (عادةً <strong>4 سنوات</strong>)</li>
                  <li>الحصول على تقييم أداء وظيفي إيجابي (جيد فأعلى)</li>
                  <li>توافر وظيفة شاغرة في المرتبة الأعلى</li>
                  <li>استيفاء متطلبات التدريب والتطوير الوظيفي المحددة من الجهة</li>
                </ul>
                <div className="alert alert-warning mt-3" style={{ fontSize: "14px" }}>
                  ⚠️ <strong>إخلاء مسؤولية:</strong> هذه الحاسبة تقديرية للاستخدام الشخصي.
                  الأرقام مبنية على سلم الرواتب المعتمد ولكن قد تختلف البدلات الإضافية حسب الجهة
                  والوظيفة. المرجع الرسمي هو{" "}
                  <a
                    href="https://hrsd.gov.sa"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    وزارة الموارد البشرية والتنمية الاجتماعية
                  </a>.
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}

export default SaudiSalaryScaleCalculator;
